import { QueryParameters } from '@deck.gl/carto'
import { ProviderType } from 'features/connections'
import { MapDataset, SqlParameter, SqlParameterItem, SqlParameterType } from 'features/maps/types'
import { removeSqlPlaceholderSyntax } from './sqlParameterUtils'
import { QueryParameterValue } from 'features/sql/types'

export function formatPlaceholder(sqlName: string): string {
  return `{{${sqlName}}}`
}

const queryPlaceholderRegexp = new RegExp(`{{(.*?)}}`, 'gm')

export function getQueryPlaceholders(query: string) {
  return query.match(queryPlaceholderRegexp)
}
export function hasSqlParametersPlaceholders(query: string) {
  return queryPlaceholderRegexp.test(query)
}

export function parseQuery({
  query,
  sqlParameters,
  provider
}: {
  query: string
  sqlParameters?: SqlParameter[] | null
  provider: ProviderType
}) {
  // We remove comments form query because reshift and postgres doesn't work well if their comments have placeholders like {{date_from}}
  const queryWithoutComments = removeSQLComments(query)
  let source = query
  let queryTemplate: string | null = null
  let queryParameters: QueryParameters | null = null
  const hasPlaceholders = hasSqlParametersPlaceholders(queryWithoutComments)

  if (hasPlaceholders) {
    queryTemplate = query
    ;({ parsedQuery: source, queryParameters } = parseQueryTemplate({
      query: queryWithoutComments,
      sqlParameters,
      provider
    }))
  }
  return { source, queryTemplate, queryParameters }
}

function parseQueryTemplate({
  query,
  sqlParameters,
  provider
}: {
  query: string
  sqlParameters?: SqlParameter[] | null
  provider: ProviderType
}) {
  let parsedQuery = query
  const queryParameters: QueryParameters = [ProviderType.BIGQUERY, ProviderType.DATABRICKS_REST].includes(provider)
    ? {}
    : []

  const index: Record<string, QueryParameterValue> = {}

  const getSqlPlaceholder = (name: string, value: unknown) => {
    switch (provider) {
      case ProviderType.BIGQUERY:
        queryParameters[name] = value
        return `@${name}`
      case ProviderType.DATABRICKS_REST:
        queryParameters[name] = value
        return `:${name}`
      default: {
        let result
        if (!(name in index)) {
          ;(queryParameters as unknown[]).push(value)
          result = index[name] = queryParameters.length
        } else {
          result = index[name]
        }
        return `$${result}`
      }
    }
  }
  parsedQuery = parsedQuery.replaceAll(queryPlaceholderRegexp, (sqlParameterPlaceholder) => {
    const item = findSqlParameterItem(sqlParameterPlaceholder, { sqlParameters })
    if (!item) {
      throw new Error(`Invalid sql parameter placeholder: ${sqlParameterPlaceholder}`)
    }
    const { sqlName, value } = item

    switch (provider) {
      case ProviderType.BIGQUERY: {
        const sqlPlaceholder = getSqlPlaceholder(sqlName, value)
        return Array.isArray(value) ? `UNNEST(${sqlPlaceholder})` : sqlPlaceholder
      }

      case ProviderType.SNOWFLAKE:
        if (Array.isArray(value)) {
          ;(queryParameters as unknown[]).push(JSON.stringify(value))
          return `( SELECT value FROM TABLE (FLATTEN( input=>parse_json(?) )) f )`
        } else {
          ;(queryParameters as unknown[]).push(value)
          return `?`
        }
      case ProviderType.DATABRICKS:
        ;(queryParameters as unknown[]).push(value)
        return `?`
      case ProviderType.DATABRICKS_REST:
        return getSqlPlaceholder(sqlName, value)
      case ProviderType.REDSHIFT:
        if (Array.isArray(value)) {
          const sqlPlaceholder = getSqlPlaceholder(sqlName, JSON.stringify(value))
          return `(select id from ( select json_parse(${sqlPlaceholder}) id union all select 0 id ) ids, ids.id id)`
        } else {
          return getSqlPlaceholder(sqlName, value)
        }
      case ProviderType.POSTGRES: {
        const sqlPlaceholder = getSqlPlaceholder(sqlName, value)
        if (Array.isArray(value)) {
          return `( SELECT ( unnest( (${sqlPlaceholder})::text[] ) ) )`
        } else {
          return sqlPlaceholder
        }
      }

      default:
        throw new Error(`Invalid provider: ${provider}`)
    }
  })

  return {
    parsedQuery,
    queryParameters
  }
}

function findSqlParameterItem(
  name: string,
  { sqlParameters }: { sqlParameters?: SqlParameter[] | null }
): SqlParameterItem<unknown> | null {
  if (sqlParameters) {
    const nameWithOutQutes = removeSqlPlaceholderSyntax(name)
    for (const item of sqlParameterItems(sqlParameters)) {
      if (item.sqlName === nameWithOutQutes) {
        return item
      }
    }
  }

  return null
}

export function* sqlParameterItems(sqlParameters: SqlParameter | SqlParameter[]) {
  if (!Array.isArray(sqlParameters)) {
    sqlParameters = [sqlParameters]
  }
  for (const p of sqlParameters) {
    switch (p.type) {
      case SqlParameterType.DateRange:
      case SqlParameterType.NumericRange:
        yield p.start
        yield p.end
        break
      case SqlParameterType.Numeric:
      case SqlParameterType.Category:
        yield p.item
        break
    }
  }
}

function removeSQLComments(sql: string) {
  // Code from https://github.com/decemberster/sql-strip-comments/blob/master/index.js
  const result = sql.replace(/("(""|[^"])*")|('(''|[^'])*')|(--[^\n\r]*)|(\/\*[\w\W]*?(?=\*\/)\*\/)/gm, (match) => {
    if (
      (match[0] === '"' && match[match.length - 1] === '"') ||
      (match[0] === "'" && match[match.length - 1] === "'")
    ) {
      return match
    }

    return ''
  })

  return result
}

export function checkDatasetUsesSqlParameter(query: string, parameter: SqlParameter) {
  for (const { sqlName } of sqlParameterItems([parameter])) {
    const placeholder = formatPlaceholder(sqlName)
    if (query.includes(placeholder)) {
      return true
    }
  }
  return false
}

export function getSqlParamtersPlaceholderSet(sqlParameters: SqlParameter[]) {
  return new Set(Array.from(sqlParameterItems(sqlParameters)).map(({ sqlName }) => formatPlaceholder(sqlName)))
}

export function hasInvalidSqlPlaceholders(queryTemplate: string, knownPlaceholders: Set<string>) {
  const match = getQueryPlaceholders(queryTemplate)
  return !match || match.some((placeholder) => !knownPlaceholders.has(placeholder))
}

export function datasetHasInvalidSqlPlaceholders(dataset: MapDataset, knownPlaceholders: Set<string>) {
  if (!dataset.queryTemplate) {
    return false
  }
  return hasInvalidSqlPlaceholders(dataset.queryTemplate, knownPlaceholders)
}
